import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { BookingLinkModal } from '../components/booking-links/BookingLinkModal';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { initials } from '../components/inbox/avatars';
import {
    CalendarDays, Plus, Copy, Trash2, Edit3, ExternalLink, Users, Clock,
    Code, Loader2, Video, MapPin, MoreVertical, PauseCircle, PlayCircle, CheckCircle2
} from 'lucide-react';
import {
    isConferenceLocation, locationLabel,
} from '../components/schedule/locationOptions';
import {
    PILL_TONE_CLASS, cancelledLine, declinedTitle, guestZoneSuffix, isGuestDeclined, showJoin, statusPill,
} from '../lib/bookingStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = window.location.origin; // Use frontend domain for public booking URLs

// F05 (Epic #532 audit): booking rows store guest-zone wall-clock as NAIVE
// strings (#539), so rendering them through new Date()/toLocale* re-labeled
// them in the ORGANIZER's browser zone with no hint they were anything else.
// These helpers derive the label straight from the string and suffix the
// booking's persisted guest_timezone. A non-naive shape falls back to the raw
// string rather than silently mislabeling; an unset guest_timezone renders
// unlabeled (pre-#539 rows).
const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const parseNaivePart = (iso) => {
    if (!iso || typeof iso !== 'string') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/.exec(iso);
    if (!m) return null;
    return { y: +m[1], mo: +m[2], d: +m[3], hhmm: m[4] };
};

const formatNaiveTime = (iso) => {
    const p = parseNaivePart(iso);
    if (!p) return '';
    const h = parseInt(p.hhmm.slice(0, 2), 10);
    if (isNaN(h) || h > 23) return '';
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 === 0 ? 12 : h % 12}:${p.hhmm.slice(3, 5)} ${ampm}`;
};

const bookingTimeText = (b) => {
    const start = parseNaivePart(b.start_time);
    if (!start) return b.start_time || '';
    // The zone suffix is shared with the cancelled/declined lines (#791 E35), so one row never mixes zones.
    const end = formatNaiveTime(b.end_time);
    return `${SHORT_MONTHS[start.mo - 1]} ${start.d}, ${start.y} · ${formatNaiveTime(b.start_time)}${end ? ` – ${end}` : ''}${guestZoneSuffix(b)}`;
};

// Conference locations render a Video badge; everything else a MapPin. Both
// the predicate and the label come from components/schedule/locationOptions so
// this page cannot drift from the editor and the Schedule tab. It already had
// drifted: the private LOCATION_BADGE_LABELS this replaces said 'Phone' and
// 'In Person' where the shared map says 'Phone call' and 'In person'
// (#514 audit A17).
const locationBadgeLabel = (loc) => {
    const label = locationLabel(loc);
    // locationLabel echoes an unknown value back unchanged. A booking link's
    // 'custom' — and any legacy value the editor heals to Custom, e.g. 'zoom'
    // (#513) — has no shared label, so it reads "Custom" here.
    return !label || label === loc ? 'Custom' : label;
};

// #684: All / Active / Paused filter over the links the page already holds.
// "Paused" is the existing is_active boolean, renamed — two states, three tabs
// (All is the absence of a filter, not a state). A link with no is_active is
// Paused: every public lookup in routes/booking.py requires is_active: True,
// so such a link cannot take bookings.
const STATUS_FILTERS = ['all', 'active', 'paused'];
const STATUS_LABELS = { all: 'All', active: 'Active', paused: 'Paused' };
const STATUS_FILTER_STORAGE_KEY = 'nurturecrm.bookingLinks.statusFilter';
const isPausedLink = (link) => link.is_active !== true;

// #757: one label for a booking's host, shared by the row and the coach
// filter. The backend names an unresolvable host "Removed user" (routes/
// booking.py _resolve_hosts); any other removed host keeps its name and is
// suffixed, so a departed coach never reads as a current one.
const REMOVED_HOST_NAME = 'Removed user';
const hostLabel = (h) => (h.removed && h.name !== REMOVED_HOST_NAME ? `${h.name} (removed)` : h.name);

// Storage throws in some privacy modes — fall back to All rather than crash.
const readStoredStatusFilter = () => {
    try {
        const stored = window.localStorage.getItem(STATUS_FILTER_STORAGE_KEY);
        return STATUS_FILTERS.includes(stored) ? stored : 'all';
    } catch {
        return 'all';
    }
};

// #675: a standalone page at its canonical /booking-links route. It used to
// double as a guest inside the calendar's Links tab (an `embedded` prop, plus a
// forwardRef exposing openCreateModal to the calendar header); that coupling is
// gone with the tab.
export default function BookingLinks() {
    const { getAuthHeader } = useAuth();
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editLink, setEditLink] = useState(null);
    const [statusFilter, setStatusFilter] = useState(readStoredStatusFilter);

    useEffect(() => {
        try { window.localStorage.setItem(STATUS_FILTER_STORAGE_KEY, statusFilter); } catch { /* storage unavailable */ }
    }, [statusFilter]);

    // One derivation feeds both the tab counts and the rendered list, so the
    // two can never disagree.
    const linksByStatus = useMemo(() => ({
        all: links,
        active: links.filter(l => !isPausedLink(l)),
        paused: links.filter(isPausedLink),
    }), [links]);
    const visibleLinks = linksByStatus[statusFilter];

    const [showEmbedModal, setShowEmbedModal] = useState(null);
    const [showBookingsModal, setShowBookingsModal] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [editAttendeesFor, setEditAttendeesFor] = useState(null);
    const [orgUsers, setOrgUsers] = useState([]);
    const [selectedAttendees, setSelectedAttendees] = useState([]);
    const [savingAttendees, setSavingAttendees] = useState(false);
    // #757: '' = All coaches; otherwise an assigned_user.user_id.
    const [coachFilter, setCoachFilter] = useState('');

    // #757: the distinct hosts across the loaded rows, in first-seen order.
    // The coach filter only appears when there are at least two.
    const bookingHosts = useMemo(() => {
        const seen = new Map();
        bookings.forEach(b => {
            const h = b.assigned_user;
            if (h?.user_id && !seen.has(h.user_id)) seen.set(h.user_id, h);
        });
        return [...seen.values()];
    }, [bookings]);
    const showCoachFilter = bookingHosts.length >= 2;
    const activeCoachFilter = showCoachFilter ? coachFilter : '';
    const shownBookings = activeCoachFilter
        ? bookings.filter(b => b.assigned_user?.user_id === activeCoachFilter)
        : bookings;

    const loadLinks = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/booking/links`, { headers: getAuthHeader() });
            setLinks(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch { toast.error('Failed to load booking links'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    useEffect(() => { loadLinks(); }, [loadLinks]);

    const deleteLink = async (id) => {
        if (!window.confirm('Delete this booking link?')) return;
        try {
            await axios.delete(`${API}/booking/links/${id}`, { headers: getAuthHeader() });
            toast.success('Deleted');
            loadLinks();
        } catch { toast.error('Failed to delete'); }
    };

    const toggleActive = async (link) => {
        try {
            // Same predicate as the tabs, so the action always matches the badge.
            const activate = isPausedLink(link);
            await axios.put(`${API}/booking/links/${link.booking_link_id}`, { is_active: activate }, { headers: getAuthHeader() });
            toast.success(activate ? 'Link activated' : 'Link paused');
            loadLinks();
        } catch { toast.error('Failed to toggle'); }
    };

    const viewBookings = async (link) => {
        setShowBookingsModal(link);
        setCoachFilter('');
        // #757 audit: drop the previous link's rows, so neither they nor their
        // coaches (and filter options) show under this link while it loads or
        // after a failed load.
        setBookings([]);
        try {
            const res = await axios.get(`${API}/booking/links/${link.booking_link_id}/bookings`, { headers: getAuthHeader() });
            setBookings(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch { toast.error('Failed to load bookings'); }
        // Also load org users for attendee picker
        try {
            const res = await axios.get(`${API}/admin/users`, { headers: getAuthHeader() });
            setOrgUsers(Array.isArray(res.data) ? res.data : []);
        } catch { /* ignore */ }
    };

    const openAttendeePicker = (booking) => {
        setEditAttendeesFor(booking);
        setSelectedAttendees(booking.additional_attendees || []);
    };

    const toggleAttendee = (userId) => {
        setSelectedAttendees(prev =>
            prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
        );
    };

    const saveAttendees = async () => {
        if (!editAttendeesFor) return;
        setSavingAttendees(true);
        try {
            await axios.put(`${API}/booking/bookings/${editAttendeesFor.booking_id}/attendees`,
                { attendee_ids: selectedAttendees }, { headers: getAuthHeader() });
            toast.success('Team members updated');
            // Refresh bookings list
            setBookings(prev => prev.map(b =>
                b.booking_id === editAttendeesFor.booking_id
                    ? { ...b, additional_attendees: selectedAttendees }
                    : b
            ));
            setEditAttendeesFor(null);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update');
        } finally {
            setSavingAttendees(false);
        }
    };

    // A new link is always active — don't leave its creator on the Paused tab,
    // where it would not appear (#684 audit F05). onSaved runs before onClose,
    // so editLink still distinguishes an edit from a create here.
    const handleSaved = () => {
        if (!editLink && statusFilter === 'paused') setStatusFilter('all');
        loadLinks();
    };

    const copyLink = (slug) => {
        navigator.clipboard.writeText(`${BASE_URL}/book/${slug}`);
        toast.success('Booking link copied!');
    };

    return (
        <div className="p-6 max-w-6xl mx-auto" data-testid="booking-links-page">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Booking Links</h1>
                    <p className="text-sm text-stone-500 mt-1">Create scheduling pages for clients and prospects to book meetings</p>
                </div>
                <Button onClick={() => { setEditLink(null); setShowCreateModal(true); }} className="bg-stone-900 text-white hover:bg-stone-800" data-testid="create-booking-link-btn">
                    <Plus className="w-4 h-4 mr-2" /> New Booking Link
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-stone-400 animate-spin" /></div>
            ) : links.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="booking-links-empty-all">
                    <CalendarDays className="w-16 h-16 text-stone-300 mb-4" />
                    <h2 className="text-lg font-semibold text-stone-700 mb-2">No booking links yet</h2>
                    <p className="text-sm text-stone-500 mb-4 max-w-md">Create a booking link to let clients and prospects schedule meetings with you or your team.</p>
                    <Button onClick={() => setShowCreateModal(true)} className="bg-stone-900 text-white hover:bg-stone-800">
                        <Plus className="w-4 h-4 mr-2" /> Create Your First Link
                    </Button>
                </div>
            ) : (
                <Tabs
                    value={statusFilter}
                    onValueChange={(v) => { if (STATUS_FILTERS.includes(v)) setStatusFilter(v); }}
                    data-testid="booking-links-status-tabs"
                >
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="booking-links-toolbar">
                        <p className="text-sm text-stone-500" data-testid="booking-links-summary">
                            {visibleLinks.length} of {links.length} {links.length === 1 ? 'link' : 'links'} · public scheduling pages for your team
                        </p>
                        <TabsList aria-label="Filter booking links by status">
                            {STATUS_FILTERS.map(f => (
                                <TabsTrigger key={f} value={f} data-testid={`status-tab-${f}`}>
                                    {STATUS_LABELS[f]}{' '}
                                    <span className="ml-1 text-xs tabular-nums text-stone-500" data-testid={`status-count-${f}`}>{linksByStatus[f].length}</span>
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </div>
                    {/* One panel per status so every tab's aria-controls resolves;
                        Radix mounts only the selected one. */}
                    {STATUS_FILTERS.map(f => (
                        <TabsContent key={f} value={f} className="mt-0" data-testid={`booking-links-panel-${f}`}>
                            {linksByStatus[f].length === 0 ? (
                                f === 'paused' ? (
                                    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="booking-links-empty-paused">
                                        <CheckCircle2 className="w-12 h-12 text-emerald-500 mb-3" />
                                        <h2 className="text-base font-semibold text-stone-700 mb-1">Nothing paused</h2>
                                        <p className="text-sm text-stone-500 max-w-md">Every booking link is live and taking bookings. Pause a link from its actions menu to take it offline without deleting it.</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="booking-links-empty-active">
                                        <PauseCircle className="w-12 h-12 text-stone-300 mb-3" />
                                        <h2 className="text-base font-semibold text-stone-700 mb-1">No active links</h2>
                                        <p className="text-sm text-stone-500 mb-4 max-w-md">All of your booking links are paused, so none of them are taking bookings right now.</p>
                                        <Button variant="outline" size="sm" onClick={() => setStatusFilter('paused')} data-testid="empty-active-show-paused">
                                            View paused links
                                        </Button>
                                    </div>
                                )
                            ) : (
                                <div className="grid gap-4">
                                    {linksByStatus[f].map(link => {
                                        const paused = isPausedLink(link);
                                        const id = link.booking_link_id;
                                        return (
                                        <div key={id} className={`min-w-0 border border-stone-200 rounded-xl p-5 transition-shadow ${paused ? 'bg-stone-50' : 'bg-white hover:shadow-sm'}`} data-testid={`booking-link-${id}`} data-status={paused ? 'paused' : 'active'}>
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex items-start gap-4 min-w-0">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${paused ? 'grayscale' : ''}`} style={{ backgroundColor: link.color + '15', color: link.color }}>
                                                        {link.booking_type === 'round_robin' ? <Users className="w-5 h-5" /> : <CalendarDays className="w-5 h-5" />}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <h3 className="font-semibold text-stone-900 truncate">{link.name}</h3>
                                                            {paused && (
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium flex-shrink-0" data-testid={`booking-link-status-${id}`}>
                                                                    <PauseCircle className="w-3 h-3" /> Paused
                                                                </span>
                                                            )}
                                                        </div>
                                                        {link.description && <p className="text-sm text-stone-500 mt-0.5 line-clamp-1">{link.description}</p>}
                                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-stone-500">
                                                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {link.duration} min</span>
                                                            <span className="px-1.5 py-0.5 rounded-full bg-stone-100 font-medium">{link.booking_type === 'round_robin' ? 'Round Robin' : '1:1'}</span>
                                                            <span className="flex items-center gap-1" data-testid={`booking-link-location-${id}`}>
                                                                {isConferenceLocation(link.location) ? <Video className="w-3 h-3" /> : <MapPin className="w-3 h-3" />}
                                                                {locationBadgeLabel(link.location)}
                                                            </span>
                                                            {link.booking_count > 0 && <span className="font-medium text-stone-700">{link.booking_count} bookings</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 flex-shrink-0" aria-label={`Actions for ${link.name}`} data-testid={`link-actions-${id}`}>
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-48">
                                                        <DropdownMenuItem onClick={() => copyLink(link.slug)} data-testid={`copy-link-${id}`}>
                                                            <Copy className="w-4 h-4 mr-2" /> Copy link
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => window.open(`/book/${link.slug}`, '_blank')} data-testid={`preview-link-${id}`}>
                                                            <ExternalLink className="w-4 h-4 mr-2" /> Open public page
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => setShowEmbedModal(link)} data-testid={`embed-link-${id}`}>
                                                            <Code className="w-4 h-4 mr-2" /> Embed code
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem onClick={() => viewBookings(link)} data-testid={`bookings-link-${id}`}>
                                                            <CalendarDays className="w-4 h-4 mr-2" /> View bookings
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => { setEditLink(link); setShowCreateModal(true); }} data-testid={`edit-link-${id}`}>
                                                            <Edit3 className="w-4 h-4 mr-2" /> Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem onClick={() => toggleActive(link)} data-testid={`toggle-active-${id}`}>
                                                            {paused
                                                                ? <><PlayCircle className="w-4 h-4 mr-2" /> Activate link</>
                                                                : <><PauseCircle className="w-4 h-4 mr-2" /> Pause link</>}
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem onClick={() => deleteLink(id)} className="text-red-600 focus:text-red-600" data-testid={`delete-link-${id}`}>
                                                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                            <div className="mt-4 flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 pl-3 pr-1 py-1">
                                                <code className={`flex-1 min-w-0 text-xs font-mono truncate ${paused ? 'text-stone-500' : 'text-stone-600'}`}>{BASE_URL}/book/{link.slug}</code>
                                                {paused && <span className="text-xs text-stone-500 flex-shrink-0" data-testid={`booking-link-paused-hint-${id}`}>Not accepting bookings</span>}
                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 flex-shrink-0" onClick={() => copyLink(link.slug)} aria-label="Copy booking link" title="Copy booking link" data-testid={`copy-link-inline-${id}`}>
                                                    <Copy className="w-3.5 h-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>
                            )}
                        </TabsContent>
                    ))}
                </Tabs>
            )}

            {/* Create/Edit Modal */}
            <BookingLinkModal
                open={showCreateModal}
                onClose={() => { setShowCreateModal(false); setEditLink(null); }}
                existing={editLink}
                getAuthHeader={getAuthHeader}
                onSaved={handleSaved}
            />

            {/* Embed Modal */}
            <EmbedModal open={!!showEmbedModal} onClose={() => setShowEmbedModal(null)} link={showEmbedModal} />

            {/* Bookings List Modal */}
            <Dialog open={!!showBookingsModal} onOpenChange={() => { setShowBookingsModal(null); setEditAttendeesFor(null); }}>
                <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="bookings-list-modal">
                    <DialogHeader>
                        <DialogTitle>Bookings — {showBookingsModal?.name}</DialogTitle>
                        <DialogDescription data-testid="bookings-count">
                            {activeCoachFilter
                                ? `${shownBookings.length} of ${bookings.length} bookings`
                                : `${bookings.length} total bookings`}
                        </DialogDescription>
                    </DialogHeader>
                    {showCoachFilter && (
                        // Native <select>: Radix ui/select is brittle under jsdom (#757).
                        <div className="flex items-center gap-2">
                            <Label htmlFor="bookings-coach-filter" className="text-xs text-stone-500">Coach</Label>
                            <select
                                id="bookings-coach-filter"
                                value={activeCoachFilter}
                                onChange={e => setCoachFilter(e.target.value)}
                                className="text-sm border border-stone-200 rounded-md px-2 py-1 bg-white text-stone-700"
                                data-testid="bookings-coach-filter"
                            >
                                <option value="">All coaches</option>
                                {bookingHosts.map(h => (
                                    <option key={h.user_id} value={h.user_id}>{hostLabel(h)}</option>
                                ))}
                            </select>
                        </div>
                    )}
                    <div className="space-y-2">
                        {bookings.length === 0 ? (
                            <p className="text-sm text-stone-500 py-8 text-center">No bookings yet</p>
                        ) : shownBookings.map(b => (
                            <div key={b.booking_id} className={`p-3 rounded-lg border text-sm ${b.status === 'cancelled' ? 'bg-stone-50 border-stone-300' : 'bg-stone-50 border-stone-100'}`} data-testid={`booking-${b.booking_id}`}>
                                <div className="flex items-center justify-between">
                                    <span className="font-medium text-stone-800">{b.guest_name} ({b.guest_email})</span>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${PILL_TONE_CLASS[statusPill(b).tone]}`} data-testid={`booking-status-${b.booking_id}`}>{statusPill(b).label}</span>
                                        {/* #791 D1: a guest's own calendar decline flags the booking; it stays confirmed. */}
                                        {isGuestDeclined(b) && (
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${PILL_TONE_CLASS.amber}`} title={declinedTitle(b)} data-testid={`booking-declined-${b.booking_id}`}>Guest declined</span>
                                        )}
                                        {b.status === 'confirmed' && (
                                            <button onClick={() => openAttendeePicker(b)}
                                                className="flex items-center gap-1 text-xs text-stone-600 hover:text-stone-800 px-2 py-0.5 rounded border border-stone-200 hover:border-stone-300 bg-white transition-colors"
                                                data-testid={`add-attendees-${b.booking_id}`}>
                                                <Users className="w-3 h-3" />
                                                {(b.additional_attendees?.length || 0) > 0
                                                    ? `${b.additional_attendees.length} added`
                                                    : 'Add Team'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {b.assigned_user && (
                                    <div
                                        className={`flex items-center gap-1.5 mt-1 text-xs ${b.assigned_user.removed ? 'text-stone-400' : 'text-stone-500'}`}
                                        data-testid={`booking-coach-${b.booking_id}`}
                                    >
                                        <Avatar className="h-5 w-5" aria-hidden="true">
                                            <AvatarFallback className="text-[9px] font-medium bg-stone-200 text-stone-600">
                                                {initials(b.assigned_user.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span>Coach: {hostLabel(b.assigned_user)}</span>
                                    </div>
                                )}
                                <p className="text-xs text-stone-500 mt-1" data-testid={`booking-time-${b.booking_id}`}>
                                    {bookingTimeText(b)}
                                </p>
                                {b.status === 'cancelled' && (
                                    <p className="text-xs text-stone-600 mt-1" data-testid={`booking-cancelled-${b.booking_id}`}>
                                        {cancelledLine(b)}
                                    </p>
                                )}
                                {b.status === 'cancelled' && b.cancel_reason && (
                                    <p className="text-xs text-stone-600 mt-1 line-clamp-2 break-words" title={b.cancel_reason} data-testid={`booking-cancel-reason-${b.booking_id}`}>
                                        {`“${b.cancel_reason}”`}
                                    </p>
                                )}
                                {(b.additional_attendees?.length || 0) > 0 && (
                                    <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                                        <span className="text-xs text-stone-400">Team:</span>
                                        {b.additional_attendees.map(uid => {
                                            const u = orgUsers.find(ou => ou.user_id === uid);
                                            return <span key={uid} className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">{u?.name || u?.email || uid}</span>;
                                        })}
                                    </div>
                                )}
                                {showJoin(b) && <a href={b.meet_link} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-600 hover:underline mt-1 inline-block">Join Meeting</a>}

                                {/* Inline Attendee Picker */}
                                {editAttendeesFor?.booking_id === b.booking_id && (
                                    <div className="mt-3 pt-3 border-t border-stone-200" data-testid="attendee-picker">
                                        <p className="text-xs font-semibold text-stone-500 uppercase mb-2">Add Team Members to Meeting</p>
                                        <div className="max-h-36 overflow-y-auto space-y-1 border border-stone-200 rounded-lg p-2 bg-white">
                                            {orgUsers.filter(u => u.user_id !== b.assigned_to).map(u => (
                                                <label key={u.user_id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-stone-50 cursor-pointer text-sm">
                                                    <Checkbox
                                                        checked={selectedAttendees.includes(u.user_id)}
                                                        onCheckedChange={() => toggleAttendee(u.user_id)}
                                                        data-testid={`attendee-check-${u.user_id}`}
                                                    />
                                                    <span className="truncate">{u.name || u.email}</span>
                                                    <span className="text-xs text-stone-400 ml-auto">{u.role}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <div className="flex items-center gap-2 mt-2">
                                            <Button size="sm" onClick={saveAttendees} disabled={savingAttendees}
                                                className="bg-stone-900 text-white hover:bg-stone-800 text-xs h-7"
                                                data-testid="save-attendees-btn">
                                                {savingAttendees ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                                                Save
                                            </Button>
                                            <Button size="sm" variant="outline" onClick={() => setEditAttendeesFor(null)} className="text-xs h-7">Cancel</Button>
                                            <span className="text-xs text-stone-400 ml-auto">{selectedAttendees.length} selected — calendar invites will be sent</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

/* ── Create/Edit Booking Link Modal ─────────────────────────────────────── */
// #685: the editor lives in components/booking-links/; re-exported here so
// existing importers (and the BookingLinks suites) keep resolving it.
export { BookingLinkModal };

/* ── Embed Code Modal ───────────────────────────────────────────────────── */
function EmbedModal({ open, onClose, link }) {
    if (!link) return null;
    const bookingUrl = `${BASE_URL}/book/${link.slug}`;
    const iframeCode = `<iframe src="${bookingUrl}?embed=true" width="100%" height="700" frameborder="0" style="border:none;border-radius:12px;"></iframe>`;
    const popupCode = `<script>
  (function(){
    var btn=document.createElement('button');
    btn.textContent='Book a Meeting';
    btn.style.cssText='background:${link.color};color:#fff;border:none;padding:12px 24px;border-radius:8px;font-size:14px;cursor:pointer;font-family:system-ui;';
    btn.onclick=function(){
      var d=document.createElement('div');
      d.style.cssText='position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';
      d.onclick=function(e){if(e.target===d)d.remove();};
      var f=document.createElement('iframe');
      f.src='${bookingUrl}?embed=true';
      f.style.cssText='width:480px;max-width:95vw;height:85vh;border:none;border-radius:16px;box-shadow:0 25px 50px rgba(0,0,0,0.25);';
      d.appendChild(f);
      document.body.appendChild(d);
    };
    document.currentScript.parentNode.insertBefore(btn,document.currentScript);
  })();
</script>`;

    const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-xl" data-testid="embed-modal">
                <DialogHeader>
                    <DialogTitle>Embed Codes — {link.name}</DialogTitle>
                    <DialogDescription>Add your booking page to any website</DialogDescription>
                </DialogHeader>
                <div className="space-y-5 py-2">
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Inline Embed (iframe)</Label>
                            <Button variant="ghost" size="sm" onClick={() => copy(iframeCode)} className="text-xs h-7"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                        </div>
                        <pre className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs font-mono text-stone-700 whitespace-pre-wrap overflow-x-auto" data-testid="embed-iframe-code">{iframeCode}</pre>
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Popup Widget (button + modal)</Label>
                            <Button variant="ghost" size="sm" onClick={() => copy(popupCode)} className="text-xs h-7"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                        </div>
                        <pre className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs font-mono text-stone-700 whitespace-pre-wrap overflow-x-auto max-h-48" data-testid="embed-popup-code">{popupCode}</pre>
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs font-semibold text-stone-500 uppercase">Direct Link</Label>
                            <Button variant="ghost" size="sm" onClick={() => copy(bookingUrl)} className="text-xs h-7"><Copy className="w-3 h-3 mr-1" /> Copy</Button>
                        </div>
                        <code className="block bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs font-mono text-stone-700" data-testid="embed-direct-link">{bookingUrl}</code>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
